export class PrivacyManager {
  private static SENSITIVE_PARAM_REGEX = /^(token|auth|password|key|ssn|jwt|api_key|access_token|secret|cvv|otp|pin|credit_card)$/i;

  private customMaskSelectors: Set<string> = new Set();
  private customIgnoreSelectors: Set<string> = new Set();

  public ignore(selectorOrElement: string | HTMLElement): void {
    if (typeof selectorOrElement === "string") {
      this.customIgnoreSelectors.add(selectorOrElement);
    } else if (selectorOrElement && selectorOrElement.setAttribute) {
      selectorOrElement.setAttribute("data-thrico-ignore", "true");
    }
  }

  public mask(selectorOrElement: string | HTMLElement): void {
    if (typeof selectorOrElement === "string") {
      this.customMaskSelectors.add(selectorOrElement);
    } else if (selectorOrElement && selectorOrElement.setAttribute) {
      selectorOrElement.setAttribute("data-thrico-private", "true");
    }
  }

  public isElementIgnored(element: HTMLElement | null): boolean {
    if (!element) return false;

    let curr: HTMLElement | null = element;
    while (curr && curr !== document.body) {
      if (
        curr.hasAttribute("data-thrico-ignore") ||
        curr.classList?.contains("thrico-ignore") ||
        curr.classList?.contains("data-private")
      ) {
        return true;
      }

      // Check input sensitive types
      if (curr instanceof HTMLInputElement) {
        const type = (curr.type || "").toLowerCase();
        const name = (curr.name || "").toLowerCase();
        const id = (curr.id || "").toLowerCase();

        if (
          type === "password" ||
          type === "hidden" ||
          name.includes("password") ||
          name.includes("cvv") ||
          name.includes("card") ||
          name.includes("otp") ||
          id.includes("password") ||
          id.includes("cvv")
        ) {
          return true;
        }
      }

      for (const sel of this.customIgnoreSelectors) {
        try {
          if (curr.matches && curr.matches(sel)) return true;
        } catch {}
      }

      curr = curr.parentElement;
    }

    return false;
  }

  public isElementMasked(element: HTMLElement | null): boolean {
    if (!element) return false;

    let curr: HTMLElement | null = element;
    while (curr && curr !== document.body) {
      if (
        curr.hasAttribute("data-thrico-private") ||
        curr.classList?.contains("thrico-private")
      ) {
        return true;
      }

      for (const sel of this.customMaskSelectors) {
        try {
          if (curr.matches && curr.matches(sel)) return true;
        } catch {}
      }

      curr = curr.parentElement;
    }

    return false;
  }

  /**
   * Redact sensitive query parameters from tracking URLs
   */
  public sanitizeUrl(rawUrl: string): string {
    if (!rawUrl) return "";
    try {
      const parsed = new URL(rawUrl, typeof window !== "undefined" ? window.location.origin : "https://thrico.com");
      const searchParams = new URLSearchParams(parsed.search);
      let changed = false;

      searchParams.forEach((val, key) => {
        if (PrivacyManager.SENSITIVE_PARAM_REGEX.test(key)) {
          searchParams.set(key, "[REDACTED]");
          changed = true;
        }
      });

      if (changed) {
        parsed.search = searchParams.toString();
        return parsed.toString();
      }
      return rawUrl;
    } catch {
      return rawUrl;
    }
  }

  /**
   * Clean text content
   */
  public sanitizeText(text: string, isMasked: boolean = false): string {
    if (!text) return "";
    if (isMasked) return "[REDACTED]";
    // Trim and limit length
    return text.trim().slice(0, 100);
  }
}
